import {
  Box,
  Button,
  Card,
  Grid,
  Heading,
  Input,
  NativeSelect,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { Field, Toggle } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'
import type { ApiError, ExtensionInstance } from '@mangovoice/types'

import { UserGroupPickList } from '../../components/UserGroupPickList'
import {
  useAutoSelectEveryoneGroup,
  useCreateSharedVoicemail,
  useGetExtensionInfo,
  useListExtensions,
  usePbxSoundClips,
  usePbxSystemDefaults,
  usePbxUserGroups,
  useUpdateExtension,
  useUpdateExtensionUserGroups,
} from '../../hooks'
import { useExtensionsI18n } from '../../i18n/provider'
import type { SharedVoicemailSeedFields } from '../../types/extension.types'
import {
  DEFAULT_VOICEMAIL_TIMEZONE,
  EXTENSION_TYPE_DEFAULT_NUMBER,
  getNextExtensionNumber,
  isReservedExtensionNumber,
  isValidEmailList,
  normalizeToLegacyTimezone,
  SWITCH_FORM_CSS,
  TOAST_DURATION_ERROR,
  TOAST_DURATION_SUCCESS,
  VOICEMAIL_TIMEZONE_OPTIONS,
} from '../../utils'
import { generateSecureFourDigitPin } from '../../utils/generateSecureFourDigitPin'
import { getListLabelForSlug, getTypeLabelForSlug } from '../typeSlugLabels'
import { renderWithWrapper } from './renderWithWrapper'
import type { ExtensionEditPageProps } from './types'

const toI18nKey = (value: string) => value.replace(/[^\w]/g, '_').toLowerCase()

export const SharedVoicemailEditPage = ({
  pbxId,
  type,
  extensionId: extensionIdParam,
  toast,
  getListRoute,
  wrapper,
  isUkEnvironment = false,
}: ExtensionEditPageProps) => {
  const { text } = useExtensionsI18n()
  const navigate = useNavigate()
  const pbxIdNum = pbxId
  const isAddMode = extensionIdParam === 'new'
  const extensionId = !isAddMode ? parseInt(extensionIdParam, 10) : undefined
  const isValidId = extensionId != null && !Number.isNaN(extensionId)

  const {
    data: extension,
    isLoading: isLoadingExtension,
    isError: isErrorExtension,
  } = useGetExtensionInfo({
    extensionId: extensionId!,
    options: { enabled: !isAddMode && isValidId },
  })

  const { soundClips } = usePbxSoundClips(pbxIdNum)
  const { extensions: allExtensions, isLoading: isLoadingAllExtensions } =
    useListExtensions({
      pbxId: pbxIdNum,
      listParams: { limit: 1000, fetchAll: true },
      options: { enabled: isAddMode },
    })
  const { userGroups: allUserGroups } = usePbxUserGroups(pbxIdNum, '')
  const { systemDefaults } = usePbxSystemDefaults(pbxIdNum)

  const createMutation = useCreateSharedVoicemail(pbxIdNum)
  const updateExtensionMutation = useUpdateExtension(pbxIdNum)
  const updateUserGroupsMutation = useUpdateExtensionUserGroups(pbxIdNum)

  const [name, setName] = useState(
    isAddMode
      ? text('extensions.types.sharedVoicemail', 'Shared Voicemail Box')
      : '',
  )
  const [number, setNumber] = useState('')
  const [omitFromDirectory, setOmitFromDirectory] = useState(isAddMode)
  const [voicemailTimeZone, setVoicemailTimeZone] = useState(
    DEFAULT_VOICEMAIL_TIMEZONE,
  )
  const [voicemailPassword, setVoicemailPassword] = useState(
    isAddMode ? generateSecureFourDigitPin() : '',
  )
  const [playVoicemailInstructions, setPlayVoicemailInstructions] =
    useState(true)
  const [sendEmailNotifications, setSendEmailNotifications] = useState(false)
  const [attachVoicemailMessage, setAttachVoicemailMessage] = useState(false)
  const [deleteMessageAfterSend, setDeleteMessageAfterSend] = useState(false)
  const [messageEmailAddress, setMessageEmailAddress] = useState('')
  const [useSystemGreetings, setUseSystemGreetings] = useState(true)
  const [outgoingMessageUnavailableId, setOutgoingMessageUnavailableId] =
    useState<string>('')
  const [userGroupIds, setUserGroupIds] = useState<number[]>([])
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  useEffect(() => {
    if (!isAddMode || isLoadingAllExtensions) return
    setNumber((prev) => {
      if (prev !== '') return prev
      const sameTypeNumbers = allExtensions
        .filter((ext) => ext.type === ExtensionType.SHARED)
        .map((ext) => ext.number)
      const allNumbers = allExtensions.map((ext) => ext.number)
      return getNextExtensionNumber(
        sameTypeNumbers,
        allNumbers,
        EXTENSION_TYPE_DEFAULT_NUMBER['shared-voicemail'] ?? 600,
        isUkEnvironment,
      )
    })
  }, [isAddMode, isLoadingAllExtensions, allExtensions, isUkEnvironment])

  useEffect(() => {
    if (extension && extension.type === ExtensionType.SHARED) {
      const sv = extension as ExtensionInstance & SharedVoicemailSeedFields
      setName(sv.name ?? '')
      setNumber(sv.number ?? '')
      setOmitFromDirectory(sv.omit_from_directory ?? false)
      setVoicemailTimeZone(
        normalizeToLegacyTimezone(
          sv.voicemail_time_zone || DEFAULT_VOICEMAIL_TIMEZONE,
        ),
      )
      setVoicemailPassword(sv.voicemail_password ?? '')
      setPlayVoicemailInstructions(sv.play_voicemail_instructions ?? true)
      setSendEmailNotifications(sv.send_email_notifications ?? false)
      setAttachVoicemailMessage(sv.attach_voicemail_message ?? false)
      setDeleteMessageAfterSend(sv.delete_message_after_send ?? false)
      setMessageEmailAddress(sv.message_email_address ?? '')
      setUseSystemGreetings(sv.use_system_greetings ?? true)
      setOutgoingMessageUnavailableId(
        sv.outgoing_message_unavailable_id != null
          ? String(sv.outgoing_message_unavailable_id)
          : '',
      )
      setUserGroupIds(sv.user_groups?.map((group) => group.id) ?? [])
    }
  }, [extension])

  // Use PBX default timezone when creating a new shared voicemail
  useEffect(() => {
    if (!isAddMode || !systemDefaults?.default_time_zone) return
    setVoicemailTimeZone(
      normalizeToLegacyTimezone(systemDefaults.default_time_zone),
    )
  }, [isAddMode, systemDefaults?.default_time_zone])

  useAutoSelectEveryoneGroup(isAddMode, allUserGroups, setUserGroupIds)

  const emailInvalid =
    attemptedSubmit &&
    sendEmailNotifications &&
    (!messageEmailAddress.trim() || !isValidEmailList(messageEmailAddress))
  const emailRequiredButEmpty =
    sendEmailNotifications && messageEmailAddress.trim() === ''
  const emailFormatInvalid =
    sendEmailNotifications &&
    messageEmailAddress.trim() !== '' &&
    !isValidEmailList(messageEmailAddress)

  const listPath = getListRoute({ pbxId: pbxIdNum, type })
  const typeLabel = getTypeLabelForSlug(type, text)
  const listLabel = getListLabelForSlug(type, text)

  const runValidation = (): boolean => {
    if (!name.trim() || !number.trim()) return false
    if (
      isReservedExtensionNumber(number.trim(), { isUkEnvironment }) &&
      (isAddMode || number.trim() !== (extension?.number ?? '').trim())
    )
      return false
    if (!/^\d{4,20}$/.test(voicemailPassword.trim())) return false
    if (emailRequiredButEmpty || emailFormatInvalid) return false
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAttemptedSubmit(true)
    if (!runValidation()) {
      const validationDescription = emailFormatInvalid
        ? text(
            'extensions.edit.emailFormatInvalid',
            'Incorrect email formatting. Make sure there are no spaces. Separate multiple addresses with commas.',
          )
        : text(
            'extensions.edit.reviewRequiredFields',
            'Please review required fields and try again.',
          )

      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: validationDescription,
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }

    const payload = {
      name: name.trim(),
      number: number.trim(),
      omit_from_directory: omitFromDirectory,
      voicemail_time_zone: voicemailTimeZone.trim() || null,
      voicemail_password: voicemailPassword.trim() || null,
      play_voicemail_instructions: playVoicemailInstructions,
      send_email_notifications: sendEmailNotifications,
      // The attach/delete toggles are only rendered when email notifications
      // are on. Omit the keys otherwise so a save can't silently zero stored
      // values the admin couldn't see (matches LineEditPage behavior).
      ...(sendEmailNotifications
        ? {
            attach_voicemail_message: attachVoicemailMessage,
            delete_message_after_send: deleteMessageAfterSend,
          }
        : {}),
      use_system_greetings: useSystemGreetings,
      outgoing_message_unavailable_id: outgoingMessageUnavailableId.trim()
        ? parseInt(outgoingMessageUnavailableId, 10)
        : null,
      ...(sendEmailNotifications
        ? { message_email_address: messageEmailAddress.trim() }
        : {}),
    }

    try {
      if (isAddMode) {
        const created = await createMutation.mutateAsync(payload as never)
        if (userGroupIds.length > 0) {
          await updateUserGroupsMutation.mutateAsync({
            extensionId: created.id,
            user_group_ids: userGroupIds,
          })
        }
        toast.create({
          title: text(
            'extensions.edit.sharedVoicemail.created',
            'Shared voicemail created',
          ),
          type: 'success',
          duration: TOAST_DURATION_SUCCESS,
          closable: true,
        })
        navigate(listPath)
        return
      }

      if (!extensionId || !extension) return
      await updateExtensionMutation.mutateAsync({
        extensionId,
        payload,
      })
      await updateUserGroupsMutation.mutateAsync({
        extensionId,
        user_group_ids: userGroupIds,
      })
      toast.create({
        title: text(
          'extensions.edit.sharedVoicemail.updated',
          'Shared voicemail updated',
        ),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
      navigate(listPath)
    } catch (error: unknown) {
      const apiErr = error as ApiError | undefined
      toast.create({
        title: isAddMode
          ? text('extensions.edit.createFailed', 'Create failed')
          : text('extensions.edit.updateFailed', 'Update failed'),
        description:
          apiErr?.message ??
          (error as Error)?.message ??
          text(
            'extensions.edit.sharedVoicemail.saveFailed',
            'Failed to save shared voicemail',
          ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
    }
  }

  const isPending =
    createMutation.isPending ||
    updateExtensionMutation.isPending ||
    updateUserGroupsMutation.isPending
  const sv = extension as
    (ExtensionInstance & SharedVoicemailSeedFields) | undefined
  const breadcrumbs = [
    { label: listLabel, path: listPath },
    {
      label: isAddMode
        ? text('extensions.edit.breadcrumbAdd', 'Add {{typeLabel}}', {
            typeLabel,
          })
        : text(
            'extensions.edit.breadcrumbEdit',
            'Edit {{typeLabel}} {{value}}',
            { typeLabel, value: extension?.number ?? extension?.id ?? '' },
          ),
    },
  ]

  if (!isAddMode && (isLoadingExtension || (isValidId && !extension))) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Box display="flex" justifyContent="center" py={12}>
        <Spinner size="xl" />
      </Box>,
    )
  }
  if (
    !isAddMode &&
    (isErrorExtension || (extension && extension.type !== ExtensionType.SHARED))
  ) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Card.Root>
        <Card.Body p={6}>
          <Heading size="md" mb={2}>
            {text(
              'extensions.edit.sharedVoicemail.notType',
              'Not a shared voicemail',
            )}
          </Heading>
          <Link
            to={listPath}
            style={{ color: 'var(--chakra-colors-blue-500)' }}
          >
            {text('extensions.edit.backToList', '← Back to {{listLabel}}', {
              listLabel: text(
                'extensions.listLabels.sharedVoicemail',
                'Shared Voicemail',
              ),
            })}
          </Link>
        </Card.Body>
      </Card.Root>,
    )
  }

  return renderWithWrapper(
    wrapper,
    breadcrumbs,
    <form onSubmit={handleSubmit}>
      <VStack gap={6} align="stretch" css={SWITCH_FORM_CSS}>
        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>{typeLabel}</Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root required invalid={attemptedSubmit && !name.trim()}>
                <Field.Label>
                  {text('extensions.columns.name', 'Name')}
                </Field.Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field.Root>
              <Field.Root required invalid={attemptedSubmit && !number.trim()}>
                <Field.Label>
                  {text('extensions.columns.number', 'Number')}
                </Field.Label>
                <Input
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                />
              </Field.Root>
              <Field.Root gridColumn="1 / -1">
                <Toggle
                  colorPalette="brand"
                  checked={omitFromDirectory}
                  onCheckedChange={(e) =>
                    setOmitFromDirectory(e.checked === true)
                  }
                >
                  {text(
                    'extensions.edit.omitFromDirectory',
                    'Omit from directory',
                  )}
                </Toggle>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text('extensions.edit.shared.generalOptions', 'General Options')}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.shared.voicemailTimeZone',
                    'Voicemail time zone',
                  )}
                </Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={voicemailTimeZone}
                    onChange={(e) => setVoicemailTimeZone(e.target.value)}
                  >
                    {VOICEMAIL_TIMEZONE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {text(
                          `extensions.timezones.${toI18nKey(option.value)}`,
                          option.label,
                        )}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.shared.voicemailPassword',
                    'Voicemail password',
                  )}
                </Field.Label>
                <Input
                  value={voicemailPassword}
                  onChange={(e) => setVoicemailPassword(e.target.value)}
                />
              </Field.Root>
              <Field.Root alignSelf="center">
                <Toggle
                  colorPalette="brand"
                  checked={playVoicemailInstructions}
                  onCheckedChange={(e) =>
                    setPlayVoicemailInstructions(e.checked === true)
                  }
                >
                  {text(
                    'extensions.edit.shared.playVoicemailInstructions',
                    'Play voicemail instructions',
                  )}
                </Toggle>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text(
                'extensions.edit.shared.notificationOptions',
                'Notification Options',
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root gridColumn="1 / -1">
                <Toggle
                  colorPalette="brand"
                  checked={sendEmailNotifications}
                  onCheckedChange={(e) =>
                    setSendEmailNotifications(e.checked === true)
                  }
                >
                  {text(
                    'extensions.edit.shared.sendEmailNotifications',
                    'Send email notifications',
                  )}
                </Toggle>
              </Field.Root>
              {sendEmailNotifications && (
                <>
                  <Field.Root
                    gridColumn="1 / -1"
                    invalid={emailInvalid}
                    required
                  >
                    <Field.Label>
                      {text(
                        'extensions.edit.shared.messageEmailAddress',
                        'Message email address',
                      )}
                    </Field.Label>
                    <Input
                      value={messageEmailAddress}
                      onChange={(e) => setMessageEmailAddress(e.target.value)}
                      placeholder={text(
                        'extensions.edit.shared.emailPlaceholder',
                        'email@example.com',
                      )}
                    />
                  </Field.Root>
                  <Field.Root alignSelf="center">
                    <Toggle
                      colorPalette="brand"
                      checked={attachVoicemailMessage}
                      onCheckedChange={(e) =>
                        setAttachVoicemailMessage(e.checked === true)
                      }
                    >
                      {text(
                        'extensions.edit.shared.attachVoicemailMessage',
                        'Attach voicemail message',
                      )}
                    </Toggle>
                  </Field.Root>
                  <Field.Root alignSelf="center">
                    <Toggle
                      colorPalette="brand"
                      checked={deleteMessageAfterSend}
                      onCheckedChange={(e) =>
                        setDeleteMessageAfterSend(e.checked === true)
                      }
                    >
                      {text(
                        'extensions.edit.shared.deleteMessageAfterSend',
                        'Delete message after send',
                      )}
                    </Toggle>
                  </Field.Root>
                </>
              )}
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text(
                'extensions.edit.shared.outgoingMessage',
                'Outgoing Message',
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root alignSelf="center">
                <Toggle
                  colorPalette="brand"
                  checked={useSystemGreetings}
                  onCheckedChange={(e) =>
                    setUseSystemGreetings(e.checked === true)
                  }
                >
                  {text(
                    'extensions.edit.shared.useSystemGreetings',
                    'Use system greetings',
                  )}
                </Toggle>
              </Field.Root>
              {!useSystemGreetings && (
                <Field.Root>
                  <Field.Label>
                    {text(
                      'extensions.edit.shared.customGreeting',
                      'Custom Greeting',
                    )}
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={outgoingMessageUnavailableId}
                      onChange={(e) =>
                        setOutgoingMessageUnavailableId(e.target.value)
                      }
                    >
                      <option value="">
                        {text('extensions.common.none', 'None')}
                      </option>
                      {soundClips.map((sound) => (
                        <option key={sound.id} value={String(sound.id)}>
                          {sound.name}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              )}
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text('extensions.edit.userGroups', 'User Groups')}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Text fontSize="sm" color="fg.muted" mb={3}>
              {text(
                'extensions.edit.sharedVoicemail.userGroupsDescription',
                'Choose which user groups should have access to this Shared Voicemail Box.',
              )}
            </Text>
            <UserGroupPickList
              allGroups={allUserGroups}
              assignedIds={userGroupIds}
              onAssignedChange={setUserGroupIds}
              assignedGroupNames={Object.fromEntries(
                sv?.user_groups?.map((group) => [group.id, group.name]) ?? [],
              )}
              disabled={isPending}
            />
          </Card.Body>
        </Card.Root>

        <Box display="flex" gap={3} pt={2} px={4} pb={3}>
          <Button
            type="submit"
            colorPalette="brand"
            color="brand.contrast"
            disabled={isPending}
            loading={isPending}
            borderRadius="4px"
          >
            {isAddMode
              ? text(
                  'extensions.edit.sharedVoicemail.createButton',
                  'Create Shared Voicemail',
                )
              : text(
                  'extensions.edit.sharedVoicemail.updateButton',
                  'Update Shared Voicemail',
                )}
          </Button>
          <Button type="button" variant="outline" borderRadius="4px" asChild>
            <Link to={listPath}>
              {text('extensions.common.cancel', 'Cancel')}
            </Link>
          </Button>
        </Box>
      </VStack>
    </form>,
  )
}
